import { useState, useEffect } from 'react';
import API from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, Users as UsersIcon, Copy, WhatsappLogo, PaperPlaneTilt, CheckCircle, Warning, ListBullets } from '@phosphor-icons/react';

const emptyForm = {
  display_name: '', access_token: '',
  api_url: 'https://crmapi.1automations.com/api/meta', api_version: 'v19.0',
  waba_id: '', phone_number_id: '', business_id: '',
};

export default function WhatsAppAPIsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [apis, setApis] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [assignApi, setAssignApi] = useState(null);
  const [testApi, setTestApi] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testText, setTestText] = useState('Test message from your WhatsApp panel');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [logsApi, setLogsApi] = useState(null);
  const [logs, setLogs] = useState([]);
  const [checkingInactivity, setCheckingInactivity] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, ad] = await Promise.all([API.get('/whatsapp/apis'), API.get('/admins').catch(() => ({ data: { admins: [] } }))]);
      setApis(a.data.apis || []);
      setAdmins(ad.data.admins || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runInactivityCheck = async () => {
    setCheckingInactivity(true);
    try {
      const res = await API.post('/whatsapp/inactivity-alerts/check-now');
      toast.success(`Inactivity check complete. ${res.data.alerts_sent} alert(s) dispatched to admins.`);
      load();
    } catch (err) {
      toast.error('Failed to run inactivity check');
    } finally {
      setCheckingInactivity(false);
    }
  };

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const openEdit = (api) => {
    setForm({
      display_name: api.display_name || '', access_token: api.access_token || '',
      api_url: api.api_url || emptyForm.api_url, api_version: api.api_version || 'v19.0',
      waba_id: api.waba_id || '', phone_number_id: api.phone_number_id || '',
      business_id: api.business_id || '',
    });
    setEditingId(api.id);
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) await API.put(`/whatsapp/apis/${editingId}`, form);
      else await API.post('/whatsapp/apis', form);
      toast.success(editingId ? 'API updated' : 'API added');
      setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this WhatsApp API? All linked chats stay visible but new messages will not be received.')) return;
    try { await API.delete(`/whatsapp/apis/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleActive = async (api) => {
    try { await API.put(`/whatsapp/apis/${api.id}`, { is_active: !api.is_active }); load(); }
    catch { toast.error('Failed'); }
  };

  const toggleAssign = async (api, adminId) => {
    const assigned = (api.assigned_to || []).includes(adminId);
    try {
      if (assigned) await API.post(`/whatsapp/apis/${api.id}/unassign`, { admin_id: adminId });
      else await API.post(`/whatsapp/apis/${api.id}/assign`, { admin_id: adminId });
      const updated = await API.get('/whatsapp/apis');
      setApis(updated.data.apis || []);
      const fresh = (updated.data.apis || []).find(x => x.id === api.id);
      if (fresh) setAssignApi(fresh);
    } catch { toast.error('Assign failed'); }
  };

  const webhookUrl = (waba_id) => `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook/${waba_id}`;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

  const runTestSend = async () => {
    if (!testApi || !testPhone.trim()) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await API.post(`/whatsapp/apis/${testApi.id}/test-send`, { phone: testPhone.trim(), text: testText });
      setTestResult(r.data);
      if (r.data.ok) toast.success('Sent! Check the recipient phone.');
      else toast.error(`Send failed: ${r.data.error || 'unknown'}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  const timeAgo = (iso) => {
    if (!iso) return '—';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  };

  const loadLogs = async (api) => {
    try {
      const r = await API.get(`/whatsapp/apis/${api.id}/logs`);
      setLogs(r.data.logs || []);
    } catch { setLogs([]); }
  };
  useEffect(() => {
    if (!logsApi) return;
    loadLogs(logsApi);
    const t = setInterval(() => loadLogs(logsApi), 3000);
    return () => clearInterval(t);
  }, [logsApi]);

  return (
    <div className="space-y-5" data-testid="whatsapp-apis-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
            <WhatsappLogo size={28} weight="fill" className="text-green-500" /> WhatsApp APIs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Add 1automations / Meta Cloud API accounts and assign them to admins</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={runInactivityCheck} disabled={checkingInactivity} className="gap-1.5 border-red-500/30 text-red-600 hover:bg-red-500/10">
            <Warning size={14} className="text-red-500" /> {checkingInactivity ? 'Checking...' : 'Check Status Alerts'}
          </Button>
          {isSuperAdmin && (
            <Button data-testid="wa-add-api-btn" onClick={openCreate}><Plus size={16} className="mr-1" /> Add API</Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : apis.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">No WhatsApp APIs yet. Click &quot;Add API&quot;.</div>
      ) : (
        <div className="grid gap-3">
          {apis.map(a => (
            <div key={a.id} className="border border-border rounded-lg p-4 bg-card" data-testid={`wa-api-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${a.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                    <h3 className="text-base font-semibold truncate">{a.display_name}</h3>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 font-mono">
                    <span>WABA: {a.waba_id}</span>
                    <span>Phone ID: {a.phone_number_id}</span>
                    <span>Version: {a.api_version}</span>
                    <span>Business: {a.business_id || '—'}</span>
                  </div>
                  <div className="mt-2 space-y-1 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-24 shrink-0">Webhook (WABA):</span>
                      <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded truncate max-w-[420px]">{webhookUrl(a.waba_id)}</code>
                      <button onClick={() => copy(webhookUrl(a.waba_id))} className="p-1 hover:bg-accent rounded" title="Copy"><Copy size={12} /></button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-24 shrink-0">Webhook (Phone):</span>
                      <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded truncate max-w-[420px]">{webhookUrl(a.phone_number_id)}</code>
                      <button onClick={() => copy(webhookUrl(a.phone_number_id))} className="p-1 hover:bg-accent rounded" title="Copy"><Copy size={12} /></button>
                    </div>
                    <p className="text-[10px] text-muted-foreground pl-24">Try either URL in MerwaAPI Webhook settings; the log viewer will confirm which one arrives.</p>
                  </div>
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px]">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${a.last_incoming_at ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'bg-amber-500/10 text-amber-700 dark:text-amber-400'}`}>
                      {a.last_incoming_at ? <CheckCircle size={12} /> : <Warning size={12} />}
                      {a.last_incoming_at ? `Incoming: ${timeAgo(a.last_incoming_at)}` : 'No incoming yet — check webhook config'}
                    </span>
                    {a.last_outgoing_at && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${a.last_send_ok ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'bg-red-500/10 text-red-700 dark:text-red-400'}`}>
                        {a.last_send_ok ? <CheckCircle size={12} /> : <Warning size={12} />}
                        Outgoing: {timeAgo(a.last_outgoing_at)} {!a.last_send_ok && a.last_send_error ? `— ${a.last_send_error.slice(0,80)}` : ''}
                      </span>
                    )}
                  </div>
                  {a.is_red_alert && (
                    <div className="mt-2.5 p-2.5 rounded-md bg-red-500/10 border border-red-500/30 flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 text-xs font-semibold text-red-600 dark:text-red-400">
                        <Warning size={16} weight="fill" className="text-red-500 shrink-0 animate-pulse" />
                        <span>RED ALERT: No incoming messages received for {a.silent_duration || '1+ hours'}! Number may have been banned or disconnected.</span>
                      </div>
                      <span className="text-[11px] font-mono text-red-600/80">Check number status</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="flex items-center gap-1.5 mr-2">
                    <span className="text-xs text-muted-foreground">Active</span>
                    <Switch checked={!!a.is_active} disabled={!isSuperAdmin} onCheckedChange={() => toggleActive(a)} data-testid={`wa-toggle-${a.id}`} />
                  </div>
                  <Button size="sm" variant="outline" data-testid={`wa-test-${a.id}`} onClick={() => { setTestApi(a); setTestResult(null); setTestPhone(''); }}><PaperPlaneTilt size={14} className="mr-1" /> Test Send</Button>
                  <Button size="sm" variant="outline" data-testid={`wa-logs-${a.id}`} onClick={() => setLogsApi(a)}><ListBullets size={14} className="mr-1" /> Webhook Log</Button>
                  {isSuperAdmin && (
                    <>
                      <Button size="sm" variant="outline" data-testid={`wa-assign-${a.id}`} onClick={() => setAssignApi(a)}><UsersIcon size={14} className="mr-1" /> Assign ({(a.assigned_to || []).length})</Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(a)}><PencilSimple size={14} /></Button>
                      <Button size="sm" variant="outline" onClick={() => remove(a.id)}><Trash size={14} /></Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Edit WhatsApp API' : 'Add WhatsApp API'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed">
              <strong>Where to get these values:</strong> Log in to your WhatsApp API provider (e.g. <a href="https://login.merawaapi.com/" target="_blank" rel="noreferrer" className="underline">login.merawaapi.com</a> / 1automations dashboard), open your Number → <em>API Details</em>. Copy the Access Token, API URL (usually <code>https://crmapi.1automations.com/api/meta</code>), API Version, WABA ID, Phone Number ID, Business ID exactly as shown.
            </div>
            <Field label="Display Name" value={form.display_name} onChange={v => setForm(f => ({ ...f, display_name: v }))} placeholder="e.g. Sales Team" testid="wa-form-name" />
            <Field label="Access Token" value={form.access_token} onChange={v => setForm(f => ({ ...f, access_token: v }))} placeholder="paste token..." testid="wa-form-token" textarea />
            <Field label="API URL" value={form.api_url} onChange={v => setForm(f => ({ ...f, api_url: v }))} testid="wa-form-url" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="API Version" value={form.api_version} onChange={v => setForm(f => ({ ...f, api_version: v }))} testid="wa-form-version" />
              <Field label="WABA ID" value={form.waba_id} onChange={v => setForm(f => ({ ...f, waba_id: v }))} testid="wa-form-waba" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone Number ID" value={form.phone_number_id} onChange={v => setForm(f => ({ ...f, phone_number_id: v }))} testid="wa-form-pnid" />
              <Field label="Business ID (optional)" value={form.business_id} onChange={v => setForm(f => ({ ...f, business_id: v }))} testid="wa-form-biz" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button data-testid="wa-form-save" onClick={save} disabled={saving || !form.display_name || !form.access_token || !form.waba_id || !form.phone_number_id}>
              {saving ? 'Saving...' : (editingId ? 'Update' : 'Add API')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assignApi} onOpenChange={o => !o && setAssignApi(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign &quot;{assignApi?.display_name}&quot; to Admins</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-1.5">
            {admins.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6">No admins yet.</div>
            ) : admins.map(ad => {
              const assigned = (assignApi?.assigned_to || []).includes(ad.id);
              return (
                <div key={ad.id} className="flex items-center justify-between px-3 py-2 rounded-md border border-border">
                  <div>
                    <div className="text-sm font-medium">{ad.name}</div>
                    <div className="text-xs text-muted-foreground">{ad.email}</div>
                  </div>
                  <Switch checked={assigned} onCheckedChange={() => toggleAssign(assignApi, ad.id)} data-testid={`wa-assign-toggle-${ad.id}`} />
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Webhook Log dialog */}
      <Dialog open={!!logsApi} onOpenChange={o => { if (!o) { setLogsApi(null); setLogs([]); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Webhook Log — &quot;{logsApi?.display_name}&quot;</DialogTitle></DialogHeader>
          <div className="text-[11px] text-muted-foreground mb-2">
            Auto-refreshing every 3s. In MerwaAPI dashboard, set your webhook URL to one of the URLs shown on this API card, then send a test message from another WhatsApp number to your business number. Any HTTP request MerwaAPI sends here will appear below within seconds — even if the payload shape is unexpected.
          </div>
          <div className="max-h-[60vh] overflow-y-auto space-y-2 border-t border-border pt-2">
            {logs.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-10">
                No webhook hits yet. Configure the URL in MerwaAPI and send a test — new entries will appear here automatically.
              </div>
            ) : logs.map(l => (
              <div key={l.id} className="rounded-md border border-border p-2 text-[11px]">
                <div className="flex items-center justify-between mb-1 text-muted-foreground">
                  <span>{new Date(l.received_at).toLocaleString()}</span>
                  <span className="font-mono truncate max-w-[220px]" title={l.headers?.['content-type']}>{l.headers?.['content-type'] || ''}</span>
                </div>
                <pre className="whitespace-pre-wrap break-all bg-muted p-2 rounded text-[10px] max-h-40 overflow-y-auto">{l.raw || '(empty body)'}</pre>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Send dialog */}
      <Dialog open={!!testApi} onOpenChange={o => { if (!o) { setTestApi(null); setTestResult(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Test Send from &quot;{testApi?.display_name}&quot;</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Recipient phone (with country code, digits only)</Label>
              <Input data-testid="wa-test-phone" value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="e.g. 919999999999" />
            </div>
            <div className="space-y-1.5">
              <Label>Message text</Label>
              <textarea data-testid="wa-test-text" value={testText} onChange={e => setTestText(e.target.value)}
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              WhatsApp Cloud API only allows unsolicited messages within 24h of a user reply. First message to a new number must use an approved template.
            </p>
            {testResult && (
              <div className={`p-3 rounded-md border text-xs ${testResult.ok ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-red-500/40 bg-red-500/5'}`}>
                <div className={`font-semibold ${testResult.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                  {testResult.ok ? 'Sent successfully' : `Failed (HTTP ${testResult.status || '?'})`}
                </div>
                {testResult.error && <div className="mt-1 text-red-700 dark:text-red-400">{testResult.error}</div>}
                <pre className="mt-2 text-[10px] whitespace-pre-wrap break-words max-h-40 overflow-y-auto opacity-70">{JSON.stringify(testResult.response, null, 2)}</pre>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTestApi(null); setTestResult(null); }}>Close</Button>
            <Button data-testid="wa-test-send-btn" onClick={runTestSend} disabled={testing || !testPhone.trim()}>{testing ? 'Sending...' : 'Send Test'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, textarea }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {textarea ? (
        <textarea data-testid={testid} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" />
      ) : (
        <Input data-testid={testid} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}
